import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { GlobalStyles } from '../styles/GlobalStyles';

// Simple theme definition
const lightTheme = {
  tokens: {
    color: {
      surface: {
        primary: '#ffffff',
        secondary: '#f8f9fa',
      },
      text: {
        primary: '#303030',
        secondary: '#595959',
        tertiary: '#8c8c8c',
      },
      border: {
        primary: '#e5e5e5',
        focusStandard: '#0a0e80',
      },
      background: {
        primary: '#ffffff',
      },
      neutral: {
        200: '#e5e5e5',
        400: '#9f9f9f',
        500: '#757575',
      },
      interaction: {
        softDefault: '#2c31c9',
        softActive: '#1a1d7a',
        mediumDefault: '#e82333',
        mediumActive: '#cc1a2d',
      },
    },
    fonts: {
      family: {
        main: 'Latam_Sans',
      },
    },
  },
};

const darkTheme = {
  tokens: {
    color: {
      surface: {
        primary: '#1a1a1a',
        secondary: '#2d2d2d',
      },
      text: {
        primary: '#ffffff',
        secondary: '#b3b3b3',
        tertiary: '#8c8c8c',
      },
      border: {
        primary: '#404040',
        focusStandard: '#6666ff',
      },
      background: {
        primary: '#1a1a1a',
      },
      neutral: {
        200: '#404040',
        400: '#737373',
        500: '#8c8c8c',
      },
      interaction: {
        softDefault: '#4d4dff',
        softActive: '#3333cc',
        mediumDefault: '#ff3366',
        mediumActive: '#cc1a4d',
      },
    },
    fonts: {
      family: {
        main: 'Latam_Sans',
      },
    },
  },
};

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('theme-mode') as ThemeMode;
    if (savedMode) return savedMode;

    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  });

  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('theme-mode', newMode);
  };

  const isDark = mode === 'dark';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('theme-mode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const theme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, isDark }}>
      <StyledThemeProvider theme={theme}>
        <GlobalStyles />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};